/**
 * NPI (National Provider Identifier) — validation and check-digit derivation.
 *
 * An NPI is a 10-POSITION numeric identifier issued by CMS: 9 significant digits plus a
 * 10th CHECK DIGIT. The check digit is a Luhn checksum computed over the constant prefix
 * "80840" (the NPI's ISO 7812 issuer prefix) followed by the 9 significant digits — see
 * the CMS NPI Check Digit Calculation standard. It is fully DERIVABLE: given the 9
 * significant digits there is exactly one valid 10th digit.
 *
 * Why this file exists (CO-3174): the Facility form pinned hospiceNpi / palliativeNpi to
 * maxlength="9" + minLength(9). The browser stopped accepting keystrokes at 9, so typing a
 * real NPI silently dropped its check digit and the form then declared the 9-digit stub
 * VALID and saved it. Every NPI stored through that form was corrupt — and an NPI is what
 * goes on an 837 claim. Every OTHER NPI field in the app already uses maxlength="10" and
 * commonVar.NPI_REGEX ("^[0-9]{10}$"); the Facility form was the outlier.
 *
 * We store TEN digits, always. Storing 9 and re-deriving the check digit downstream would
 * mean recomputing it on every outbound claim and — worse — would throw away our only way
 * to detect a mistyped NPI: with the check digit present, a single-digit typo or a
 * transposition fails the checksum. That is the entire point of having one.
 */

/** Strip everything that is not a digit (users paste NPIs with spaces and dashes). */
export function digitsOnly(value: string): string {
  return (value || "").replace(/\D/g, "");
}

/**
 * The check digit for a 9-significant-digit NPI.
 *
 * Luhn over "80840" + the 9 digits (14 characters), doubling every second digit from the
 * right. Returns -1 if the input is not exactly 9 digits.
 *
 * Worked example — the CMS documentation's own sample, 1234567893:
 *   "80840" + "123456789" → checksum 67 → (10 - 67 % 10) % 10 = 3 → NPI 1234567893.
 */
export function npiCheckDigit(nineDigits: string): number {
  const base = digitsOnly(nineDigits);
  if (base.length !== 9) {
    return -1;
  }

  const payload = "80840" + base;
  let sum = 0;
  let double = true; // the rightmost character of `payload` is doubled

  for (let i = payload.length - 1; i >= 0; i--) {
    let d = parseInt(payload.charAt(i), 10);
    if (double) {
      d *= 2;
      if (d > 9) {
        d -= 9;
      }
    }
    sum += d;
    double = !double;
  }

  return (10 - (sum % 10)) % 10;
}

/** True when `value` is a complete, checksum-correct 10-digit NPI. */
export function isValidNpi(value: string): boolean {
  const npi = digitsOnly(value);
  if (npi.length !== 10) {
    return false;
  }
  return npiCheckDigit(npi.substring(0, 9)) === parseInt(npi.charAt(9), 10);
}

/**
 * Normalize what the user typed into a storable 10-digit NPI.
 *
 *   * 9 digits  → the check digit is DERIVED and appended (this is what the user asked
 *                 for: "if they only put 9 can we produce the check digit" — yes, exactly
 *                 one 10th digit is valid).
 *   * 10 digits → returned as-is IF the check digit is correct. A wrong check digit means
 *                 the user mistyped: we must NOT "repair" it by overwriting the 10th digit,
 *                 because we cannot know which of the 10 digits was wrong. It is rejected
 *                 by the validator instead.
 *   * anything else (including empty) → returned as the digits we have, unchanged. NPI is
 *                 optional on this form; an empty value must stay empty.
 */
export function normalizeNpi(value: string): string {
  const npi = digitsOnly(value);

  if (npi.length === 9) {
    return npi + String(npiCheckDigit(npi));
  }
  return npi;
}

/**
 * Angular validator: OPTIONAL, but if present must be a usable NPI.
 *
 * Accepts an empty value (NPI is not required on the Facility form), a 9-digit value (we
 * derive the check digit on save), or a 10-digit value whose check digit is CORRECT.
 * Rejects a 10-digit value that fails the checksum — that is a typo, and silently storing
 * it is how a claim gets denied.
 */
export function npiValidator(control: { value: any }): {
  [key: string]: any;
} | null {
  const raw = control && control.value ? String(control.value) : "";
  const npi = digitsOnly(raw);

  if (npi.length === 0) {
    return null; // optional
  }
  if (npi.length === 9) {
    return null; // check digit derived on save
  }
  if (npi.length === 10) {
    return isValidNpi(npi) ? null : { npiCheckDigit: true };
  }
  return { npiLength: true };
}



// WEBPACK FOOTER //
// ./src/utils/npi.ts